import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../utils/analytics';
import {
  readOnboardingState,
  sanitizeOnboardingTemplateRef,
  writeOnboardingState,
} from '../utils/onboarding';

export default function OnboardingStart() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { availableTemplates = [] } = useData() || {};
  const { user } = useAuth();
  const stored = readOnboardingState(typeof localStorage !== 'undefined' ? localStorage : null, user?.id);
  const queryRef = sanitizeOnboardingTemplateRef(searchParams.get('template'));
  const templateRef = queryRef || stored.templateRef;

  const template = useMemo(() => {
    if (!templateRef) return null;
    return availableTemplates.find((item) => item.id === templateRef || item.seo_slug === templateRef) || null;
  }, [availableTemplates, templateRef]);

  useEffect(() => {
    if (typeof localStorage !== 'undefined' && user?.id && queryRef) {
      writeOnboardingState(localStorage, { status: stored.status === 'activated' ? 'activated' : 'pending', templateRef: queryRef }, user.id);
    }
    track('onboarding_viewed', { source: templateRef ? 'template_intent' : 'generic' });
  }, [user?.id]);

  const continueTemplate = () => {
    if (!template) return;
    track('template_selected', { source: 'onboarding', template_ref: templateRef });
    navigate(`/novo-laudo?templateId=${encodeURIComponent(template.id)}`);
  };

  return (
    <div className="w-full max-w-full px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-8 sm:mb-10">
          <h1 className="font-display text-[clamp(2rem,9vw,3.25rem)] font-bold leading-[1.02] tracking-tight text-navy">Crie seu primeiro laudo agora.</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">Leva cerca de 2 minutos. Só o nome do paciente e o conteúdo são obrigatórios.</p>
        </div>

        <div className="grid w-full gap-4">
          {template ? (
            <button type="button" onClick={continueTemplate} className="group w-full rounded-card border border-navy/20 bg-navy p-5 text-left shadow-card transition-transform hover:-translate-y-0.5 sm:p-6">
              <p className="text-xs font-semibold text-white/65">Modelo que você escolheu</p>
              <p className="mt-1 truncate text-lg font-semibold text-white">{template.name}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white">Continuar com este modelo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
            </button>
          ) : (
            <Link
              to="/novo-laudo"
              onClick={() => track('onboarding_action_selected', { action: 'first_report_direct' })}
              className="group w-full rounded-card border border-navy/20 bg-navy p-5 text-left shadow-card transition-transform hover:-translate-y-0.5 sm:p-6"
            >
              <span className="inline-flex items-center gap-2 text-lg font-semibold text-white">Criar meu primeiro laudo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              <p className="mt-1 text-sm leading-relaxed text-white/70">Você pode escolher um modelo dentro do editor.</p>
            </Link>
          )}

          <Link
            to="/modelos"
            onClick={() => track('onboarding_action_selected', { action: 'browse_templates' })}
            className="w-full rounded-card border border-line bg-paper px-5 py-4 text-sm font-semibold text-navy transition-colors hover:border-navy/30 hover:bg-paper-alt sm:w-auto"
          >
            Ver modelos
          </Link>
        </div>

        <p className="mt-6 text-sm leading-relaxed text-muted">O rascunho fica salvo na conta. Para emitir, você vai precisar cadastrar sua assinatura.</p>
      </div>
    </div>
  );
}
